import { mapGetters } from "vuex";
import { 
	XMarkIcon, 
	PlayIcon, 
	PauseIcon, 
	SpeakerWaveIcon, 
	SpeakerXMarkIcon,
	ArrowsPointingOutIcon 
} from "@heroicons/vue/24/outline";

export default {
	name: "HelpModal",
	components: {
		XMarkIcon,
		PlayIcon,
		PauseIcon,
		SpeakerWaveIcon,
		SpeakerXMarkIcon,
		ArrowsPointingOutIcon,
	},
	props: ["isVisible"],
	data() {
		return {
			currentTutorial: 0,
			tutorials: [
				{
					id: 1,
					titleKey: "click and drag to move around. click '+' or '-' to zoom in and out.",
					image: "/images/tutorial/t1.png",
				},
				{
					id: 2,
					titleKey: "click on the 'hotspots' to explore the factory.",
					image: "/images/tutorial/t2.png",
				},
				{
					id: 3,
					titleKey: "filter hotspots by clicking on the layers menu.",
					image: "/images/tutorial/t3.png",
				},
				{
					id: 4,
					titleKey: "explore specifics with the 'Solution Portfolio' and 'Key Drivers'.",
					image: "/images/tutorial/t4.png",
				},
			],
			isPlaying: false,
			currentTime: 0,
			duration: 0,
			volume: 1,
			isMuted: false,
			showControls: false,
		};
	},
	watch: {
		isVisible(newVal) {
			const event = new CustomEvent("modal-state-change", {
				detail: { isOpen: newVal },
			});
			document.dispatchEvent(event);
			
			if (newVal) {
				this.currentTutorial = 0;
			}
		},
	},
	computed: {
		...mapGetters("languages", ["activeLanguage"]),
		...mapGetters("staticContent", ["getTranslations"]),
		lastTutorial() {
			return this.currentTutorial === this.tutorials.length - 1;
		},
		firstTutorial() {
			return this.currentTutorial === 0;
		},
		currentTutorialData() {
			return {
				...this.tutorials[this.currentTutorial],
				title: this.getTranslations(this.tutorials[this.currentTutorial].titleKey)
			};
		},
		videoSrc() {
			return this.activeLanguage?.help_url || `https://files.diverseygps.com/cms-uploads/v/help-en.mp4`;
		},
		progressPercentage() {
			return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
		},
	},
	methods: {
		close() {
			this.$emit("update:isVisible", false);
		},
		runAfterLeave() {
			// No action needed for help modal
		},
		nextTutorial() {
			if (this.lastTutorial) {
				this.close();
				return;
			}
			this.currentTutorial++;
		},
		prevTutorial() {
			if (this.currentTutorial === 0) {
				return;
			}
			this.currentTutorial--;
		},
		
		// Video control methods
		togglePlay() {
			const video = this.$refs.videoElement;
			if (this.isPlaying) {
				video.pause();
			} else {
				video.play();
			}
		},
		
		onPlay() {
			this.isPlaying = true;
		},
		
		onPause() {
			this.isPlaying = false;
		},
		
		onEnded() {
			this.isPlaying = false;
		},
		
		onLoadedMetadata() {
			const video = this.$refs.videoElement;
			this.duration = video.duration;
		},
		
		onTimeUpdate() {
			const video = this.$refs.videoElement;
			this.currentTime = video.currentTime;
		},
		
		seekTo(event) {
			const video = this.$refs.videoElement;
			const rect = event.currentTarget.getBoundingClientRect();
			const clickX = event.clientX - rect.left;
			const percentage = clickX / rect.width;
			const newTime = percentage * this.duration;
			video.currentTime = newTime;
		},
		
		toggleMute() {
			const video = this.$refs.videoElement;
			if (this.isMuted) {
				video.muted = false;
				this.isMuted = false;
			} else {
				video.muted = true;
				this.isMuted = true;
			}
		},
		
		setVolume(event) {
			const video = this.$refs.videoElement;
			const newVolume = parseFloat(event.target.value);
			video.volume = newVolume;
			this.volume = newVolume;
			this.isMuted = newVolume === 0;
		},
		
		toggleFullscreen() {
			const videoContainer = this.$refs.videoElement.parentElement;
			if (!document.fullscreenElement) {
				videoContainer.requestFullscreen().catch(err => {
					console.log(`Error attempting to enable fullscreen: ${err.message}`);
				});
			} else {
				document.exitFullscreen();
			}
		},
		
		formatTime(seconds) {
			if (!seconds || isNaN(seconds)) return '0:00';
			const mins = Math.floor(seconds / 60);
			const secs = Math.floor(seconds % 60);
			return `${mins}:${secs.toString().padStart(2, '0')}`;
		},
	},
};